import React, { useState } from 'react';
import CrawlImages from '../CrawlImages';
import {
  CrawlFbResponse,
  GiftRecommendationResponse,
} from '../../../../../api/crawlFb';
import { useGetGiftRecommendations } from '../../../../../hooks/useCrawlFb';
import GiftRecommendationModal from './GiftRecommendationModal';
import { formatCurrencyWithoutSymbol } from '@/lib/utils';

interface CompletedStatusProps {
  crawlData: CrawlFbResponse;
  onNewCrawl: () => void;
  isShowNewCrawl: boolean;
  onImageSelect?: (imageData: { imageUrl: string; title: string }) => void;
}

interface ProfileData {
  name?: string;
  age?: number | null;
  gender?: string;
  occupation?: string;
  location?: string;
  so_Followers?: number;
  so_Following?: number;
  posts?: Array<{
    time: string;
    likes: number;
    comments: number;
    description: string;
    people?: Array<{
      gender: string;
      age_range: string;
      body_type: string;
      skin_tone: string;
    }>;
    products?: Array<{
      type: string;
      color: string;
      material: string;
      style: string;
      gender: string;
      season: string;
    }>;
  }>;
  work_education?: {
    work?: Array<{
      work?: string;
      time?: string;
    }>;
    education?: Array<{
      edu?: string;
      time?: string;
    }>;
  };
  life_events?: Record<string, string>;
  details?: {
    name?: string;
    followers?: string;
    following?: string;
    about?: string;
    favorite_quotes?: string;
  };
}

const WorkSection: React.FC<{
  work: Array<{ work?: string; time?: string }>;
}> = ({ work }) => {
  if (!work || work.length === 0) return null;

  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-white/50">
      <div className="flex items-center mb-4">
        <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3">
          <svg
            className="w-4 h-4 text-white"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M20,6C20.58,6 21.05,6.2 21.42,6.59C21.8,7 22,7.45 22,8V19C22,19.55 21.8,20 21.42,20.41C21.05,20.8 20.58,21 20,21H4C3.42,21 2.95,20.8 2.58,20.41C2.2,20 2,19.55 2,19V8C2,7.45 2.2,7 2.58,6.59C2.95,6.2 3.42,6 4,6H8V4C8,3.42 8.2,2.95 8.58,2.58C8.95,2.2 9.42,2 10,2H14C14.58,2 15.05,2.2 15.42,2.58C15.8,2.95 16,3.42 16,4V6H20M4,8V19H20V8H4M10,4V6H14V4H10Z" />
          </svg>
        </div>
        <span className="font-bold text-lg text-gray-800">Công việc</span>
      </div>

      <div className="space-y-4">
        {work.map((workItem, index) => (
          <div
            key={index}
            className={`bg-gradient-to-r ${
              index === 0
                ? 'from-green-50 to-emerald-50'
                : 'from-blue-50 to-indigo-50'
            } rounded-xl p-4 border ${
              index === 0 ? 'border-green-200/50' : 'border-blue-200/50'
            }`}
          >
            <div className="flex items-start">
              <div
                className={`w-3 h-3 ${
                  index === 0 ? 'bg-green-500' : 'bg-blue-500'
                } rounded-full mt-2 mr-3 flex-shrink-0`}
              ></div>
              <div>
                <div className="text-gray-700 font-medium">{workItem.work}</div>
                {workItem.time && (
                  <div className="text-gray-500 text-xs mt-1 italic">
                    {workItem.time}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const EducationSection: React.FC<{
  education: Array<{ edu?: string; time?: string }>;
}> = ({ education }) => {
  if (!education || education.length === 0) return null;

  return (
    <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-white/50">
      <div className="flex items-center mb-4">
        <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
          <svg
            className="w-4 h-4 text-white"
            fill="currentColor"
            viewBox="0 0 24 24"
          >
            <path d="M12,3L1,9L12,15L21,10.09V17H23V9L12,3M5,13.18V17.18L12,21L19,17.18V13.18L12,17L5,13.18Z" />
          </svg>
        </div>
        <span className="font-bold text-lg text-gray-800">Học vấn</span>
      </div>

      <div className="space-y-4">
        {education.map((eduItem, index) => (
          <div
            key={index}
            className={`bg-gradient-to-r ${
              index === 0
                ? 'from-purple-50 to-pink-50'
                : 'from-orange-50 to-red-50'
            } rounded-xl p-4 border ${
              index === 0 ? 'border-purple-200/50' : 'border-orange-200/50'
            }`}
          >
            <div className="flex items-start">
              <div
                className={`w-3 h-3 ${
                  index === 0 ? 'bg-purple-500' : 'bg-orange-500'
                } rounded-full mt-2 mr-3 flex-shrink-0`}
              ></div>
              <div>
                <div className="text-gray-700">{eduItem.edu}</div>
                {eduItem.time && (
                  <div className="text-gray-500 text-xs mt-1 italic">
                    {eduItem.time}
                  </div>
                )}
              </div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

const ProfileDisplay: React.FC<{ data: ProfileData }> = ({ data }) => {
  // Calculate post analytics
  const postAnalytics = data.posts
    ? {
        totalPosts: data.posts.length,
        totalLikes: data.posts.reduce(
          (sum, post) => sum + (post.likes || 0),
          0
        ),
        totalComments: data.posts.reduce(
          (sum, post) => sum + (post.comments || 0),
          0
        ),
        avgLikes: Math.round(
          data.posts.reduce((sum, post) => sum + (post.likes || 0), 0) /
            data.posts.length
        ),
        avgComments: Math.round(
          data.posts.reduce((sum, post) => sum + (post.comments || 0), 0) /
            data.posts.length
        ),
        topPost: data.posts.reduce(
          (max, post) => ((post.likes || 0) > (max.likes || 0) ? post : max),
          data.posts[0]
        ),
      }
    : null;

  // Parse life events chronologically
  const chronologicalEvents = data.life_events
    ? Object.entries(data.life_events)
        .map(([year, event]) => ({ year: parseInt(year.split('_')[0]), event }))
        .sort((a, b) => a.year - b.year)
    : [];

  // Parse multiple quotes
  const quotes = data.details?.favorite_quotes
    ? data.details.favorite_quotes.split('\n').filter((q) => q.trim())
    : [];

  return (
    <div className="space-y-8 animate-in slide-in-from-bottom-4 duration-700">
      {/* Basic Info */}
      <div className="bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 rounded-2xl p-8 border border-blue-200/50 shadow-xl hover:shadow-2xl transition-all duration-500 backdrop-blur-sm">
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-xl flex items-center justify-center shadow-lg">
            <svg
              className="w-6 h-6 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
            </svg>
          </div>
          <h6 className="text-2xl font-bold text-gray-800 ml-4">
            Thông tin cá nhân
          </h6>
        </div>

        {/* Main profile card */}
        {data.name && (
          <div className="bg-white/90 backdrop-blur rounded-3xl p-8 shadow-2xl border border-white/70 mb-6 transform hover:scale-105 transition-all duration-500">
            <div className="flex items-center justify-center mb-6">
              <div className="w-28 h-28 bg-gradient-to-br from-blue-400 via-purple-500 to-pink-500 rounded-full flex items-center justify-center shadow-2xl ring-4 ring-white/50">
                <span className="text-4xl font-bold text-white">
                  {data.name.charAt(0).toUpperCase()}
                </span>
              </div>
            </div>
            <div className="text-center">
              <h2 className="text-3xl font-bold text-gray-800 mb-2">
                {data.name}
              </h2>
              {data.occupation && (
                <p className="text-lg text-gray-600 mb-4 font-medium">
                  {data.occupation}
                </p>
              )}
              {data.location && (
                <div className="flex items-center justify-center text-gray-500 mb-4">
                  <div className="w-5 h-5 bg-red-100 rounded-full flex items-center justify-center mr-2">
                    <svg
                      className="w-3 h-3 text-red-500"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12,11.5A2.5,2.5 0 0,1 9.5,9A2.5,2.5 0 0,1 12,6.5A2.5,2.5 0 0,1 14.5,9A2.5,2.5 0 0,1 12,11.5M12,2A7,7 0 0,0 5,9C5,14.25 12,22 12,22C12,22 19,14.25 19,9A7,7 0 0,0 12,2Z" />
                    </svg>
                  </div>
                  <span>{data.location}</span>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Stats grid */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          {data.age !== undefined && (
            <div className="group">
              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50 transform hover:-translate-y-2">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-purple-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12,4A4,4 0 0,1 16,8A4,4 0 0,1 12,12A4,4 0 0,1 8,8A4,4 0 0,1 12,4M12,14C16.42,14 20,15.79 20,18V20H4V18C4,15.79 7.58,14 12,14Z" />
                    </svg>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-1">
                  {data.age || '-'}
                </div>
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Tuổi
                </div>
              </div>
            </div>
          )}

          {data.gender && (
            <div className="group">
              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50 transform hover:-translate-y-2">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-pink-500 to-rose-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M12,4A6,6 0 0,1 18,10C18,12.97 15.84,15.44 13,15.92V18H15V20H13V22H11V20H9V18H11V15.92C8.16,15.44 6,12.97 6,10A6,6 0 0,1 12,4M12,6A4,4 0 0,0 8,10A4,4 0 0,0 12,14A4,4 0 0,0 16,10A4,4 0 0,0 12,6Z" />
                    </svg>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-1">
                  {data.gender || '-'}
                </div>
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Giới tính
                </div>
              </div>
            </div>
          )}

          {((data?.details?.followers !== undefined &&
            data?.details?.followers !== null) ||
            (data?.so_Followers !== undefined &&
              data?.so_Followers !== null)) && (
            <div className="group">
              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50 transform hover:-translate-y-2">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M16,4C18.21,4 20,5.79 20,8C20,10.21 18.21,12 16,12C13.79,12 12,10.21 12,8C12,5.79 13.79,4 16,4M16,14C20.42,14 24,15.79 24,18V20H8V18C8,15.79 11.58,14 16,14Z" />
                    </svg>
                  </div>
                </div>
                <div className="text-3xl font-bold text-gray-800 mb-1">
                  {data?.so_Followers
                    ? formatCurrencyWithoutSymbol(data?.so_Followers)
                    : data?.details?.followers
                        ?.toString()
                        ?.replace('followers', '') || '0'}
                </div>
                <div className="text-sm font-semibold text-gray-500 uppercase tracking-wide">
                  Followers
                </div>
              </div>
            </div>
          )}

          {((data.so_Following !== undefined && data.so_Following !== null) ||
            (data.details?.following !== undefined &&
              data.details?.following !== null)) && (
            <div className="group">
              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border border-white/50 transform hover:-translate-y-2">
                <div className="flex items-center justify-between mb-4">
                  <div className="w-12 h-12 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-2xl flex items-center justify-center shadow-lg">
                    <svg
                      className="w-6 h-6 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M15,14C17.67,14 23,15.33 23,18V20H7V18C7,15.33 12.33,14 15,14M15,12A4,4 0 0,1 11,8A4,4 0 0,1 15,4A4,4 0 0,1 19,8A4,4 0 0,1 15,12M5,9.59L7.12,7.46L8.54,8.88L5,12.41L1.46,8.88L2.88,7.46L5,9.59Z" />
                    </svg>
                  </div>
                </div>
                <div className="text-3xl font-bold text-blue-700 mb-1">
                  {data.so_Following
                    ? formatCurrencyWithoutSymbol(data.so_Following)
                    : data.details?.following
                        ?.toLocaleString()
                        ?.replace('following', '') || '0'}
                </div>
                <div className="text-sm font-semibold text-blue-600 uppercase tracking-wide">
                  Following
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Work & Education */}
      {data.work_education && (
        <div className="space-y-6">
          <WorkSection work={data.work_education.work || []} />
          <EducationSection education={data.work_education.education || []} />
        </div>
      )}

      {/* Post Analytics */}
      {postAnalytics && (
        <div className="bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-50 rounded-2xl p-8 border border-violet-200/50 shadow-xl hover:shadow-2xl transition-all duration-500">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-violet-500 to-purple-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M16,11V3H8v6H2v12h20V11H16z M10,5h4v14h-4V5z M4,11h4v8H4V11z M20,19h-4v-6h4V19z" />
              </svg>
            </div>
            <h6 className="text-2xl font-bold text-gray-800 ml-4">
              Phân tích bài đăng
            </h6>
          </div>

          <div className="grid grid-cols-2 gap-4 mb-6">
            <div className="bg-gradient-to-br from-indigo-100 to-indigo-200 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-8 h-8 bg-indigo-500 rounded-lg mx-auto mb-3 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
                </svg>
              </div>
              <div className="text-3xl font-bold text-indigo-700 mb-1">
                {postAnalytics.totalPosts}
              </div>
              <div className="text-sm font-medium text-indigo-600">
                Tổng bài đăng
              </div>
            </div>

            <div className="bg-gradient-to-br from-emerald-100 to-emerald-200 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-8 h-8 bg-emerald-500 rounded-lg mx-auto mb-3 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
                </svg>
              </div>
              <div className="text-3xl font-bold text-emerald-700 mb-1">
                {postAnalytics.totalLikes.toLocaleString()}
              </div>
              <div className="text-sm font-medium text-emerald-600">
                Tổng likes
              </div>
            </div>

            <div className="bg-gradient-to-br from-blue-100 to-blue-200 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-8 h-8 bg-blue-500 rounded-lg mx-auto mb-3 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M21.99 4c0-1.1-.89-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z" />
                </svg>
              </div>
              <div className="text-3xl font-bold text-blue-700 mb-1">
                {postAnalytics.totalComments.toLocaleString()}
              </div>
              <div className="text-sm font-medium text-blue-600">
                Tổng comments
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-100 to-orange-200 rounded-2xl p-6 text-center shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
              <div className="w-8 h-8 bg-orange-500 rounded-lg mx-auto mb-3 flex items-center justify-center">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                </svg>
              </div>
              <div className="text-3xl font-bold text-orange-700 mb-1">
                {postAnalytics.avgLikes.toLocaleString()}
              </div>
              <div className="text-sm font-medium text-orange-600">
                TB likes/bài
              </div>
            </div>
          </div>

          {postAnalytics.topPost && (
            <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-2xl p-6 border border-amber-200/50 shadow-lg">
              <div className="flex items-center mb-4">
                <div className="w-8 h-8 bg-gradient-to-r from-amber-500 to-yellow-500 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
                  </svg>
                </div>
                <span className="font-bold text-amber-800 text-lg">
                  Bài đăng hot nhất
                </span>
              </div>
              <p className="text-gray-700 text-base mb-4 leading-relaxed">
                {postAnalytics.topPost.description}
              </p>
              <div className="flex gap-6 text-sm">
                <div className="flex items-center bg-white/70 rounded-lg px-3 py-2">
                  <span className="text-green-600 mr-2">👍</span>
                  <span className="font-semibold text-gray-700">
                    {postAnalytics.topPost.likes?.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center bg-white/70 rounded-lg px-3 py-2">
                  <span className="text-blue-600 mr-2">💬</span>
                  <span className="font-semibold text-gray-700">
                    {postAnalytics.topPost.comments?.toLocaleString()}
                  </span>
                </div>
                <div className="flex items-center bg-white/70 rounded-lg px-3 py-2">
                  <span className="text-purple-600 mr-2">📅</span>
                  <span className="font-semibold text-gray-700">
                    {postAnalytics.topPost.time}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Posts */}
      {data.posts && data.posts.length > 0 && (
        <div className="bg-gradient-to-br from-rose-50 via-pink-50 to-fuchsia-50 rounded-2xl p-8 border border-rose-200/50 shadow-xl hover:shadow-2xl transition-all duration-500">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-rose-500 to-pink-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm-5 14H7v-2h7v2zm3-4H7v-2h10v2zm0-4H7V7h10v2z" />
              </svg>
            </div>
            <h6 className="text-2xl font-bold text-gray-800 ml-4">
              Bài đăng gần đây ({data.posts.length} bài)
            </h6>
          </div>

          <div className="space-y-4 max-h-96 overflow-y-auto pr-2">
            {data.posts.map((post, index) => (
              <div
                key={index}
                className="group bg-white/90 backdrop-blur rounded-2xl p-6 border border-white/70 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1"
              >
                <div className="flex justify-between items-start mb-4">
                  <div className="bg-gradient-to-r from-blue-100 to-cyan-100 rounded-full px-4 py-2 border border-blue-200/50">
                    <span className="text-sm font-semibold text-blue-700">
                      {post.time}
                    </span>
                  </div>
                  <div className="flex gap-4">
                    <div className="flex items-center bg-gradient-to-r from-green-100 to-emerald-100 rounded-full px-3 py-1 border border-green-200/50">
                      <svg
                        className="w-3 h-3 text-green-600 mr-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M1 21h4V9H1v12zm22-11c0-1.1-.9-2-2-2h-6.31l.95-4.57.03-.32c0-.41-.17-.79-.44-1.06L14.17 1 7.59 7.59C7.22 7.95 7 8.45 7 9v10c0 1.1.9 2 2 2h9c.83 0 1.54-.5 1.84-1.22l3.02-7.05c.09-.23.14-.47.14-.73v-2z" />
                      </svg>
                      <span className="text-xs font-semibold text-green-700">
                        {post.likes?.toLocaleString()}
                      </span>
                    </div>
                    <div className="flex items-center bg-gradient-to-r from-purple-100 to-violet-100 rounded-full px-3 py-1 border border-purple-200/50">
                      <svg
                        className="w-3 h-3 text-purple-600 mr-1"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M21.99 4c0-1.1-.89-2-2-2H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h14l4 4-.01-18z" />
                      </svg>
                      <span className="text-xs font-semibold text-purple-700">
                        {post.comments?.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>

                <p className="text-gray-700 text-base leading-relaxed mb-4 line-clamp-3 group-hover:line-clamp-none transition-all duration-300">
                  {post.description}
                </p>

                <div className="space-y-3">
                  {post.people && post.people.length > 0 && (
                    <div className="bg-gradient-to-r from-amber-50 to-yellow-50 rounded-xl p-3 border border-amber-200/50">
                      <div className="flex items-center mb-2">
                        <svg
                          className="w-4 h-4 text-amber-600 mr-2"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zM4 18v-4h3v4h2v-7.5c0-.83.67-1.5 1.5-1.5S12 9.67 12 10.5V15h2v3h3v-3h1v5H4z" />
                        </svg>
                        <span className="text-sm font-semibold text-amber-700">
                          Đối tượng
                        </span>
                      </div>
                      <div className="text-sm text-amber-700">
                        {post.people.map((p, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-white/60 rounded-lg px-2 py-1 mr-2 mb-1"
                          >
                            {p.gender} {p.age_range}, {p.body_type}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}

                  {post.products && post.products.length > 0 && (
                    <div className="bg-gradient-to-r from-indigo-50 to-blue-50 rounded-xl p-3 border border-indigo-200/50">
                      <div className="flex items-center mb-2">
                        <svg
                          className="w-4 h-4 text-indigo-600 mr-2"
                          fill="currentColor"
                          viewBox="0 0 24 24"
                        >
                          <path d="M19 7h-3V6a4 4 0 0 0-8 0v1H5a1 1 0 0 0-1 1v11a3 3 0 0 0 3 3h10a3 3 0 0 0 3-3V8a1 1 0 0 0-1-1zM10 6a2 2 0 0 1 4 0v1h-4V6zm8 15H6a1 1 0 0 1-1-1V9h2v1a1 1 0 0 0 2 0V9h4v1a1 1 0 0 0 2 0V9h2v11a1 1 0 0 1-1 1z" />
                        </svg>
                        <span className="text-sm font-semibold text-indigo-700">
                          Sản phẩm
                        </span>
                      </div>
                      <div className="text-sm text-indigo-700">
                        {post.products.map((p, idx) => (
                          <span
                            key={idx}
                            className="inline-block bg-white/60 rounded-lg px-2 py-1 mr-2 mb-1"
                          >
                            {p.type} {p.color}
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Life Events Timeline */}
      {chronologicalEvents.length > 0 && (
        <div className="bg-gradient-to-br from-amber-50 via-orange-50 to-yellow-50 rounded-2xl p-8 border border-amber-200/50 shadow-xl hover:shadow-2xl transition-all duration-500">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-amber-500 to-orange-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z" />
              </svg>
            </div>
            <h6 className="text-2xl font-bold text-gray-800 ml-4">
              Timeline cuộc đời ({chronologicalEvents.length} sự kiện)
            </h6>
          </div>

          <div className="relative max-h-80 overflow-y-auto pr-2">
            <div className="absolute left-8 top-0 bottom-0 w-0.5 bg-gradient-to-b from-amber-300 to-orange-400"></div>

            <div className="space-y-6">
              {chronologicalEvents.map((item, index) => (
                <div key={index} className="relative flex items-start group">
                  <div className="flex-shrink-0 w-16 h-16 bg-gradient-to-br from-amber-100 to-orange-200 rounded-2xl flex items-center justify-center shadow-lg border-4 border-white z-10 group-hover:scale-110 transition-transform duration-300">
                    <span className="font-bold text-amber-700 text-sm">
                      {item.year}
                    </span>
                  </div>

                  <div className="flex-1 ml-6 bg-white/90 backdrop-blur rounded-2xl p-4 shadow-lg border border-white/70 group-hover:shadow-xl transition-all duration-300">
                    <div className="text-gray-700 font-medium leading-relaxed">
                      {item.event}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* Additional Details */}
      {data.details && (
        <div className="bg-gradient-to-br from-slate-50 via-gray-50 to-zinc-50 rounded-2xl p-8 border border-slate-200/50 shadow-xl hover:shadow-2xl transition-all duration-500">
          <div className="flex items-center mb-6">
            <div className="w-12 h-12 bg-gradient-to-r from-slate-500 to-gray-600 rounded-xl flex items-center justify-center shadow-lg">
              <svg
                className="w-6 h-6 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M14,2H6A2,2 0 0,1 4,4V20A2,2 0 0,1 6,22H18A2,2 0 0,1 20,20V8L14,2M18,20H6V4H13V9H18V20Z" />
              </svg>
            </div>
            <h6 className="text-2xl font-bold text-gray-800 ml-4">
              Thông tin bổ sung
            </h6>
          </div>

          <div className="space-y-6">
            {data.details.about && (
              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-white/50">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-cyan-600 rounded-lg flex items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
                    </svg>
                  </div>
                  <span className="font-bold text-lg text-gray-800">
                    Giới thiệu
                  </span>
                </div>
                <div className="bg-gradient-to-r from-blue-50 to-cyan-50 rounded-2xl p-6 border border-blue-200/50">
                  <p className="text-gray-700 text-base leading-relaxed italic">
                    "{data.details.about}"
                  </p>
                </div>
              </div>
            )}

            {quotes.length > 0 && (
              <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-white/50">
                <div className="flex items-center mb-4">
                  <div className="w-8 h-8 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg flex items-center justify-center mr-3">
                    <svg
                      className="w-4 h-4 text-white"
                      fill="currentColor"
                      viewBox="0 0 24 24"
                    >
                      <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
                    </svg>
                  </div>
                  <span className="font-bold text-lg text-gray-800">
                    Câu nói yêu thích ({quotes.length} câu)
                  </span>
                </div>
                <div className="space-y-4">
                  {quotes.map((quote, index) => (
                    <div
                      key={index}
                      className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-2xl p-4 border border-purple-200/50"
                    >
                      <p className="text-gray-700 text-base leading-relaxed italic">
                        "{quote.trim()}"
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

const CompletedStatus: React.FC<CompletedStatusProps> = ({
  crawlData,
  onNewCrawl,
  isShowNewCrawl,
  onImageSelect,
}) => {
  const [isGiftModalOpen, setIsGiftModalOpen] = useState(false);
  const [giftData, setGiftData] = useState<GiftRecommendationResponse | null>(
    crawlData.userEvent &&
      crawlData.userEvent.length > 0 &&
      crawlData.selectedGifts &&
      Object.keys(crawlData.selectedGifts).length > 0
      ? {
          user_event: crawlData.userEvent,
          selected_gifts: crawlData.selectedGifts,
        }
      : null
  );

  const giftRecommendationMutation = useGetGiftRecommendations();

  const handleGetGiftRecommendations = async () => {
    try {
      const response = await giftRecommendationMutation.mutateAsync(
        crawlData.id
      );
      setGiftData(response);
      setIsGiftModalOpen(true);
    } catch (error) {
      console.error('Error getting gift recommendations:', error);
    }
  };

  return (
    <div className="bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 border border-green-200/50 rounded-3xl p-8 shadow-xl hover:shadow-2xl transition-all duration-500 backdrop-blur-sm">
      <div className="flex items-center justify-between mb-8">
        <div className="flex items-center">
          <div className="relative">
            <div className="w-16 h-16 bg-gradient-to-r from-green-500 via-emerald-600 to-teal-700 rounded-2xl flex items-center justify-center shadow-xl mr-5">
              <svg
                className="w-8 h-8 text-white"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm-2 15l-5-5 1.41-1.41L10 14.17l7.59-7.59L19 8l-9 9z" />
              </svg>
            </div>
            <div className="absolute -top-2 -right-2 w-6 h-6 bg-yellow-400 rounded-full flex items-center justify-center shadow-lg animate-bounce">
              <span className="text-xs">✨</span>
            </div>
          </div>
          <div>
            <h4 className="text-2xl font-bold text-green-800 flex items-center mb-2">
              <span className="mr-3 text-2xl">🎉</span>
              Hoàn thành xuất sắc!
            </h4>
            <p className="text-green-700 text-base font-medium">
              AI đã hoàn thành phân tích và xử lý tất cả ảnh một cách chi tiết
            </p>
            <div className="flex items-center mt-2">
              <div className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></div>
              <span className="text-green-600 text-sm font-semibold">
                Tất cả dữ liệu đã được xử lý thành công
              </span>
            </div>
          </div>
        </div>
        {isShowNewCrawl && (
          <button
            onClick={onNewCrawl}
            className="group bg-gradient-to-r from-blue-500 via-indigo-600 to-purple-700 hover:from-blue-600 hover:via-indigo-700 hover:to-purple-800 text-white rounded-2xl px-8 py-4 shadow-xl hover:shadow-2xl transition-all duration-300 transform hover:-translate-y-1 font-bold text-lg"
          >
            <div className="flex items-center">
              <span className="mr-3 text-xl group-hover:animate-bounce">
                🚀
              </span>
              <div className="text-left">
                <div>Crawl mới</div>
                <div className="text-xs opacity-90 font-normal">
                  Bắt đầu phân tích mới
                </div>
              </div>
            </div>
          </button>
        )}
      </div>

      {/* Enhanced Progress indicator - completed */}
      <div className="mb-8">
        <div className="bg-white/80 backdrop-blur rounded-2xl p-6 border border-white/70 shadow-lg">
          <div className="flex items-center justify-between mb-4">
            <div className="flex items-center">
              <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-xl flex items-center justify-center mr-3">
                <svg
                  className="w-4 h-4 text-white"
                  fill="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path d="M13,9H11V7H13M13,17H11V11H13M12,2A10,10 0 0,0 2,12A10,10 0 0,0 12,22A10,10 0 0,0 22,12A10,10 0 0,0 12,2Z" />
                </svg>
              </div>
              <span className="font-bold text-gray-800 text-lg">
                Tiến trình hoàn thành
              </span>
            </div>
            <div className="bg-gradient-to-r from-green-100 to-emerald-100 rounded-full px-4 py-2 border border-green-200/50">
              <span className="text-sm font-bold text-green-700 flex items-center">
                <span className="w-2 h-2 bg-green-500 rounded-full mr-2 animate-pulse"></span>
                100% Hoàn thành
              </span>
            </div>
          </div>

          <div className="relative">
            <div className="flex-1 bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 rounded-full h-4 overflow-hidden shadow-inner">
              <div
                className="bg-gradient-to-r from-green-400 via-emerald-500 to-green-600 h-full rounded-full shadow-lg animate-pulse"
                style={{ width: '100%' }}
              >
                <div className="h-full bg-gradient-to-r from-white/30 to-transparent rounded-full"></div>
              </div>
            </div>
            <div className="absolute inset-y-0 left-1/2 transform -translate-x-1/2 flex items-center">
              <div className="w-6 h-6 bg-white rounded-full shadow-lg flex items-center justify-center border-2 border-green-500">
                <div className="w-2 h-2 bg-green-500 rounded-full"></div>
              </div>
            </div>
          </div>

          <div className="flex justify-between mt-3 text-xs text-gray-500">
            <span>Bắt đầu</span>
            <span className="font-semibold text-green-600">Hoàn thành</span>
            <span>Kết thúc</span>
          </div>
        </div>
      </div>

      {/* AI Results */}
      {crawlData.jsonAI && (
        <div className="mb-6">
          <div className="flex items-center justify-between mb-3">
            <h5 className="text-lg font-semibold text-green-800 flex items-center">
              <span className="mr-2">🤖</span>
              Kết quả AI
            </h5>

            {/* Gift Recommendation Button */}
            <div className="flex gap-3">
              {giftData && (
                <button
                  onClick={() => setIsGiftModalOpen(true)}
                  className="group bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-700 hover:from-pink-600 hover:via-purple-700 hover:to-indigo-800 text-white rounded-2xl px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 font-medium"
                >
                  <div className="flex items-center">
                    <span className="mr-2 text-lg group-hover:animate-bounce">
                      🎁
                    </span>
                    <div className="text-left">
                      <div className="text-sm">Xem quà tặng</div>
                      <div className="text-xs opacity-90 font-normal">
                        Đã có gợi ý
                      </div>
                    </div>
                  </div>
                </button>
              )}

              <button
                onClick={handleGetGiftRecommendations}
                disabled={giftRecommendationMutation.isPending}
                className="cursor-pointer group bg-gradient-to-r from-pink-500 via-purple-600 to-indigo-700 hover:from-pink-600 hover:via-purple-700 hover:to-indigo-800 disabled:from-gray-400 disabled:via-gray-500 disabled:to-gray-600 text-white rounded-2xl px-6 py-3 shadow-lg hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1 disabled:hover:translate-y-0 font-medium disabled:cursor-not-allowed"
              >
                <div className="flex items-center">
                  {giftRecommendationMutation.isPending ? (
                    <>
                      <div className="animate-spin w-4 h-4 border-2 border-white/30 border-t-white rounded-full mr-2"></div>
                      <div className="text-left">
                        <div className="text-sm">Đang phân tích...</div>
                        <div className="text-xs opacity-90 font-normal">
                          Vui lòng chờ
                        </div>
                      </div>
                    </>
                  ) : (
                    <>
                      <span className="mr-2 text-lg group-hover:animate-bounce">
                        🎁
                      </span>
                      <div className="text-left">
                        <div className="text-sm">Gợi ý quà tặng</div>
                        <div className="text-xs opacity-90 font-normal">
                          AI phân tích
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </button>
            </div>
          </div>

          <div className="bg-white rounded-lg p-4 border border-green-200 shadow-sm">
            <div className="prose prose-sm max-w-none">
              {typeof crawlData.jsonAI === 'string' ? (
                (() => {
                  try {
                    const parsedData = JSON.parse(crawlData.jsonAI);
                    console.log(parsedData);

                    return <ProfileDisplay data={parsedData as ProfileData} />;
                  } catch {
                    return (
                      <div className="whitespace-pre-wrap text-gray-800">
                        {crawlData.jsonAI}
                      </div>
                    );
                  }
                })()
              ) : (
                <ProfileDisplay data={crawlData.jsonAI as ProfileData} />
              )}
            </div>
          </div>
        </div>
      )}

      {/* Show all crawled images */}
      <CrawlImages crawlData={crawlData} onImageSelect={onImageSelect} />

      {/* Additional Info */}
      <div className="mt-8 bg-gradient-to-br from-indigo-50 via-blue-50 to-cyan-50 rounded-2xl p-8 border border-indigo-200/50 shadow-xl hover:shadow-2xl transition-all duration-500">
        <div className="flex items-center mb-6">
          <div className="w-12 h-12 bg-gradient-to-r from-indigo-500 to-blue-600 rounded-xl flex items-center justify-center shadow-lg">
            <svg
              className="w-6 h-6 text-white"
              fill="currentColor"
              viewBox="0 0 24 24"
            >
              <path d="M16,4C16,2.89 16.89,2 18,2A2,2 0 0,1 20,4V8A2,2 0 0,1 18,10C16.89,10 16,9.11 16,8V4M18,12A4,4 0 0,1 22,16V18A2,2 0 0,1 20,20H16V22H14V20H10V22H8V20H4A2,2 0 0,1 6,18V16A4,4 0 0,1 10,12H18M4,16V18H6V16H4M8,16V18H10V16H8M12,16V18H14V16H12M16,16V18H18V16H16M20,16V18H22V16H20Z" />
            </svg>
          </div>
          <h6 className="text-2xl font-bold text-gray-800 ml-4">
            Thông tin bạn bè đã crawl
          </h6>
        </div>

        {crawlData.friendsUrls.length > 0 ? (
          <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-white/50">
            <div className="flex items-center justify-between mb-4">
              <div className="flex items-center">
                <div className="w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-lg flex items-center justify-center mr-3">
                  <svg
                    className="w-4 h-4 text-white"
                    fill="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path d="M10,4H4C2.89,4 2,4.89 2,6V18A2,2 0 0,0 4,20H20A2,2 0 0,0 22,18V8C22,6.89 21.1,6 20,6H12L10,4Z" />
                  </svg>
                </div>
                <span className="font-bold text-lg text-gray-800">
                  Friends URLs tìm được
                </span>
              </div>
              <div className="bg-gradient-to-r from-blue-100 to-indigo-100 rounded-full px-4 py-2 border border-blue-200/50">
                <span className="text-sm font-bold text-blue-700">
                  {crawlData.friendsUrls.length - 1} URLs
                </span>
              </div>
            </div>

            <div className="grid gap-3 max-h-64 overflow-y-auto pr-2">
              {crawlData.friendsUrls?.slice(1, 10).map((url, index) => (
                <a
                  key={url}
                  href={url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="group bg-gradient-to-r from-blue-50 to-indigo-50 hover:from-blue-100 hover:to-indigo-100 rounded-xl p-4 border border-blue-200/50 transition-all duration-300 hover:shadow-md transform hover:-translate-y-0.5"
                >
                  <div className="flex items-center">
                    <div className="w-8 h-8 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-lg flex items-center justify-center mr-3 group-hover:scale-110 transition-transform duration-300">
                      <span className="text-white font-bold text-sm">
                        {index + 1}
                      </span>
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="text-blue-700 font-medium text-sm truncate group-hover:text-blue-800 transition-colors">
                        {url}
                      </div>
                    </div>
                    <div className="ml-3">
                      <svg
                        className="w-4 h-4 text-blue-500 group-hover:text-blue-600 transition-colors"
                        fill="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path d="M14,3V5H17.59L7.76,14.83L9.17,16.24L19,6.41V10H21V3M19,19H5V5H12V3H5C3.89,3 3,3.89 3,5V19A2,2 0 0,0 5,21H19A2,2 0 0,0 21,19V12H19V19Z" />
                      </svg>
                    </div>
                  </div>
                </a>
              ))}
            </div>
          </div>
        ) : (
          <div className="bg-white/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-white/50 text-center">
            <div className="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
              <svg
                className="w-8 h-8 text-gray-400"
                fill="currentColor"
                viewBox="0 0 24 24"
              >
                <path d="M12,2A2,2 0 0,1 14,4C14,4.74 13.6,5.39 13,5.73V7H14A7,7 0 0,1 21,14H22A1,1 0 0,1 23,15V18A1,1 0 0,1 22,19H21V20A2,2 0 0,1 19,22H5A2,2 0 0,1 3,20V19H2A1,1 0 0,1 1,18V15A1,1 0 0,1 2,14H3A7,7 0 0,1 10,7H11V5.73C10.4,5.39 10,4.74 10,4A2,2 0 0,1 12,2M7.5,13A2.5,2.5 0 0,0 5,15.5A2.5,2.5 0 0,0 7.5,18A2.5,2.5 0 0,0 10,15.5A2.5,2.5 0 0,0 7.5,13M16.5,13A2.5,2.5 0 0,0 14,15.5A2.5,2.5 0 0,0 16.5,18A2.5,2.5 0 0,0 19,15.5A2.5,2.5 0 0,0 16.5,13Z" />
              </svg>
            </div>
            <p className="text-gray-500 text-lg font-medium">
              Chưa có thông tin bạn bè
            </p>
            <p className="text-gray-400 text-sm mt-1">
              Sẽ được cập nhật sau khi crawl hoàn tất
            </p>
          </div>
        )}
      </div>

      {/* Gift Recommendation Modal */}
      <GiftRecommendationModal
        isOpen={isGiftModalOpen}
        onClose={() => setIsGiftModalOpen(false)}
        giftData={giftData}
      />
    </div>
  );
};

export default CompletedStatus;
